<template>
  <div class="concrete-page">
    <!-- Top bar : usine / mélange selectors + daily production button -->
    <div class="concrete-topbar">
      <v-select
        v-model="selectedUsine"
        :items="usineOptions"
        :label="$t('views.dashboard.concrete.topbar.usine')"
        dense
        outlined
        hide-details
        class="concrete-select"
      />
      <v-select
        v-model="selectedMix"
        :items="mixOptions"
        :label="$t('views.dashboard.concrete.topbar.mix')"
        dense
        outlined
        hide-details
        class="concrete-select concrete-select--mix"
      />
      <v-spacer />
      <v-btn
        outlined
        class="concrete-prod-btn"
        @click="productionDialog = true"
      >
        <v-icon
          left
          small
        >
          $fas fa-box
        </v-icon>
        {{ $t('views.dashboard.concrete.topbar.production') }}
        <v-chip
          small
          label
          color="primary"
          text-color="white"
          class="ml-2"
        >
          {{ totalVolLabel }}
        </v-chip>
      </v-btn>
    </div>

    <!-- Fill layout : left data column + right charts (12-row CSS grid) -->
    <div class="concrete-content">
      <!-- LEFT : stacked tables + summary -->
      <div class="concrete-tables">
        <TableContent
          class="concrete-table-dosage"
          :title="$t('views.dashboard.concrete.dosage.title')"
          :columns="dosageColumns"
          :items="dosageRows"
          :summary-row="chartSummaryType.TOTAL"
          :full-height="!isMobile"
          dense
          disable-pagination
          fixed-header
          height="100%"
          hide-default-footer
        />
        <TableContent
          class="concrete-table-results"
          :title="$t('views.dashboard.concrete.results.title')"
          :columns="resultColumns"
          :items="resultRows"
          :full-height="!isMobile"
          dense
          disable-pagination
          fixed-header
          height="100%"
          hide-default-footer
        />
        <core-card class="concrete-summary-card">
          <div class="concrete-section-label">
            {{ $t('views.dashboard.concrete.count.title') }}
          </div>
          <div class="concrete-kpis">
            <div class="concrete-kpi">
              <div class="concrete-kpi-label">
                {{ $t('views.dashboard.concrete.count.total') }}
              </div>
              <div class="concrete-kpi-value">
                {{ mix.count.total }}
              </div>
              <div class="concrete-kpi-sub">
                {{ $t('views.dashboard.concrete.count.loads') }}
              </div>
            </div>
            <div class="concrete-kpi">
              <div
                class="concrete-kpi-label"
                :style="{ color: statusColor('success') }"
              >
                {{ $t('views.dashboard.concrete.count.conform') }}
              </div>
              <div
                class="concrete-kpi-value"
                :style="{ color: statusColor('success') }"
              >
                {{ mix.count.conformes }}
              </div>
            </div>
            <div class="concrete-kpi">
              <div
                class="concrete-kpi-label"
                :style="{ color: statusColor('error') }"
              >
                {{ $t('views.dashboard.concrete.count.critical') }}
              </div>
              <div
                class="concrete-kpi-value"
                :style="{ color: statusColor('error') }"
              >
                {{ mix.count.critiques }}
              </div>
            </div>
          </div>

          <div class="concrete-section-label">
            {{ $t('views.dashboard.concrete.chantier.title') }}
          </div>
          <div class="concrete-moy-row">
            <span>{{ $t('views.dashboard.concrete.chantier.water') }}</span>
            <span class="concrete-moy-value">
              {{ waterAverage }}
              {{ $t('views.dashboard.concrete.chantier.unitL') }}
            </span>
          </div>
          <div class="concrete-moy-row">
            <span>{{ $t('views.dashboard.concrete.chantier.transit') }}</span>
            <span class="concrete-moy-value">
              {{ transitAverage }}
              {{ $t('views.dashboard.concrete.chantier.unitMin') }}
              <trend-chip :risk="transitRisk" />
            </span>
          </div>
        </core-card>
      </div>

      <!-- RIGHT : deviation line chart (top) + 3 control bar charts (bottom) -->
      <LineChart
        id="concreteDeviation"
        class="concrete-cell-deviation"
        :title="$t('views.dashboard.concrete.deviation.title')"
        :subtitle="$t('views.dashboard.concrete.deviation.subtitle')"
        :data="deviationData"
        :category-labels="deviationLabels"
        unit="%"
        :unit-position="chartUnitPosition.AXIS_LABEL"
      />

      <BarChart
        id="concreteAir"
        class="concrete-cell-air"
        :title="$t('views.dashboard.concrete.charts.air')"
        :subtitle="
          $t('views.dashboard.concrete.charts.airTarget', {
            n: mix.res.air.cible,
          })
        "
        :legend-primary="$t('views.dashboard.concrete.charts.measured')"
        :items="airItems"
        unit="%"
        :unit-position="chartUnitPosition.AXIS_LABEL"
        :value-scale="2"
        :status-messages="statusMessages"
      />
      <BarChart
        id="concreteSlump"
        class="concrete-cell-slump"
        :title="$t('views.dashboard.concrete.charts.slump')"
        :subtitle="
          $t('views.dashboard.concrete.charts.slumpTarget', {
            n: mix.res.slump.cible,
          })
        "
        :legend-primary="$t('views.dashboard.concrete.charts.measured')"
        :items="slumpItems"
        unit="mm"
        :unit-position="chartUnitPosition.AXIS_LABEL"
        :value-scale="20"
        :status-messages="statusMessages"
      />
      <BarChart
        id="concreteTemp"
        class="concrete-cell-temp"
        :title="$t('views.dashboard.concrete.charts.temp')"
        :subtitle="$t('views.dashboard.concrete.charts.tempSubtitle')"
        :legend-primary="$t('views.dashboard.concrete.charts.measured')"
        :items="tempItems"
        unit="°C"
        :unit-position="chartUnitPosition.AXIS_LABEL"
        :value-scale="10"
        :status-messages="statusMessages"
      />
    </div>

    <!-- Daily production modal -->
    <v-dialog
      v-model="productionDialog"
      max-width="520"
    >
      <v-card class="concrete-modal">
        <v-card-title class="concrete-modal-header">
          <span>{{
            $t('views.dashboard.concrete.modal.title', { usine: usineName })
          }}</span>
          <v-spacer />
          <v-btn
            icon
            @click="productionDialog = false"
          >
            <v-icon>$fas fa-xmark</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="concrete-modal-hint">
            {{ $t('views.dashboard.concrete.modal.hint') }}
          </p>
          <div
            v-for="summary in mixSummaries"
            :key="summary.label"
            class="concrete-mix-row"
            :class="{ active: summary.active }"
            role="button"
            tabindex="0"
            @click="selectMix(summary.label)"
            @keydown.enter="selectMix(summary.label)"
          >
            <div>
              <div class="concrete-mix-code">
                {{ summary.label }} — {{ summary.mpa }} MPa
              </div>
              <div class="concrete-mix-proj">
                {{ summary.proj }}
              </div>
              <div class="concrete-mix-bar-wrap">
                <div
                  class="concrete-mix-bar"
                  :style="{
                    width: summary.pct + '%',
                    backgroundColor: summary.active ? primaryColor : '#888780',
                  }"
                />
              </div>
            </div>
            <div class="text-right">
              <div class="concrete-mix-vol">
                {{ summary.vol.toFixed(1) }} m³
              </div>
              <div class="concrete-mix-vol-lbl">
                {{ summary.pct }}%
                {{ $t('views.dashboard.concrete.modal.ofTotal') }}
              </div>
              <div class="concrete-mix-count">
                {{
                  $t('views.dashboard.concrete.modal.loads', {
                    n: summary.count,
                  })
                }}
              </div>
            </div>
          </div>
          <div class="concrete-modal-total">
            <span>{{ $t('views.dashboard.concrete.modal.total') }}</span>
            <span>{{ totalVolLabel }}</span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LineChart from '../Charts/Lines/LineChart.vue'
import BarChart from '../Charts/Bar/BarChart.vue'
import TableContent from '../Charts/Tables/TableContent.vue'
import TrendChip from '../Charts/TrendChip.vue'
import {
  chartStatus,
  chartSummaryType,
  chartUnitPosition,
  risk,
} from '@/utils/enum'
import {
  CONCRETE_DATA,
  CONCRETE_USINE,
  CONCRETE_CONSTITUENTS,
  CONCRETE_DOSAGE_PARAMS,
} from '@/mock/concreteProduction'

export default {
  name: 'ConcreteTechnicianMock',
  components: {
    LineChart,
    BarChart,
    TableContent,
    TrendChip,
  },
  data() {
    return {
      chartUnitPosition,
      chartSummaryType,
      selectedUsine: CONCRETE_USINE.id,
      selectedMix: Object.keys(CONCRETE_DATA)[0],
      productionDialog: false,
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown
    },
    primaryColor() {
      return this.$vuetify.theme.themes.light.primary
    },
    usineName() {
      return CONCRETE_USINE.name
    },
    usineOptions() {
      return [
        {
          value: CONCRETE_USINE.id,
          text: `${CONCRETE_USINE.name} (${CONCRETE_USINE.id})`,
        },
      ]
    },
    mixOptions() {
      return Object.values(CONCRETE_DATA).map((d) => ({
        value: d.label,
        text: `${d.label} — ${d.proj} (${d.mpa} MPa)`,
      }))
    },
    mix() {
      return CONCRETE_DATA[this.selectedMix]
    },
    lastBav() {
      return this.mix.bav[0]
    },
    lastChantier() {
      return this.mix.chantier[0]
    },
    // ── Left panel : dosage table (TableContent) ──
    dosageColumns() {
      return [
        {
          header: {
            text: this.$t('views.dashboard.concrete.dosage.param'),
            align: 'start',
            sortable: false,
          },
          cell: { key: 'name' },
        },
        {
          header: {
            text: this.$t('views.dashboard.concrete.dosage.theoretical'),
            align: 'end',
            sortable: false,
          },
          cell: { key: 'th', decimals: 1, displayInSummaryRow: true },
        },
        {
          header: {
            text: this.$t('views.dashboard.concrete.dosage.target'),
            align: 'end',
            sortable: false,
          },
          cell: { key: 'cible', decimals: 1, displayInSummaryRow: true },
        },
        {
          header: {
            text: this.$t('views.dashboard.concrete.dosage.actual'),
            align: 'end',
            sortable: false,
          },
          cell: { key: 'act', decimals: 1, displayInSummaryRow: true },
        },
      ]
    },
    dosageRows() {
      return CONCRETE_DOSAGE_PARAMS.map((param) => {
        const dev = this.lastBav[param.devKey]
        return {
          name: this.$t(`views.dashboard.concrete.dosage.${param.label}`),
          th: this.mix.th[param.thKey],
          cible: this.mix.cible[param.thKey],
          act: this.lastBav[param.actKey],
          status: this.dosageStatus(dev, param.warn, param.crit),
        }
      })
    },
    // ── Left panel : production results table (TableContent) ──
    resultColumns() {
      return [
        {
          header: {
            text: this.$t('views.dashboard.concrete.results.param'),
            align: 'start',
            sortable: false,
          },
          cell: { key: 'name' },
        },
        {
          header: {
            text: this.$t('views.dashboard.concrete.results.last'),
            align: 'end',
            sortable: false,
          },
          cell: { key: 'last' },
        },
        {
          header: {
            text: this.$t('views.dashboard.concrete.results.formula'),
            align: 'end',
            sortable: false,
          },
          cell: { key: 'form' },
        },
      ]
    },
    resultRows() {
      const res = this.mix.res
      const lastCh = this.lastChantier
      return [
        {
          name: this.$t('views.dashboard.concrete.results.wcm'),
          last: res.wcm.val.toFixed(3),
          form: res.wcm.cible.toFixed(2),
          status: this.percentStatus(res.wcm.val, res.wcm.cible, 2, 5),
        },
        {
          name: this.$t('views.dashboard.concrete.results.air'),
          last: lastCh.air.toFixed(1),
          form: res.air.cible.toFixed(1),
          status: this.airStatus(lastCh.air, res.air.cible),
        },
        {
          name: this.$t('views.dashboard.concrete.results.slump'),
          last: lastCh.slump,
          form: res.slump.cible,
          status: this.slumpStatus(lastCh.slump, res.slump.cible),
        },
        {
          name: this.$t('views.dashboard.concrete.results.temp'),
          last: lastCh.temp.toFixed(1),
          form: this.$t('views.dashboard.concrete.results.tempRange'),
          status: this.tempStatus(lastCh.temp),
        },
        {
          name: this.$t('views.dashboard.concrete.results.density'),
          last: res.mv.val,
          form: res.mv.cible,
          status: this.percentStatus(res.mv.val, res.mv.cible, 1, 2),
        },
        {
          name: this.$t('views.dashboard.concrete.results.fc28'),
          last: res.fc28.val.toFixed(1),
          form: res.fc28.cible,
          status: null,
        },
      ]
    },
    // ── Left panel : chantier averages ──
    waterAverage() {
      return this.average(this.mix.chantier.map((c) => c.water)).toFixed(1)
    },
    transitAverage() {
      return Math.round(this.average(this.mix.chantier.map((c) => c.transit)))
    },
    transitStatusValue() {
      return this.transitStatus(this.transitAverage)
    },
    transitRisk() {
      // Map the status to the same risk icon used in the tables (triangle / circle).
      if (this.transitStatusValue === chartStatus.WARNING) return risk.AR
      if (this.transitStatusValue === chartStatus.ERROR) return risk.HR
      return undefined
    },
    // ── Deviation line chart (6 constituents over the last 10 loads) ──
    chronologicalBav() {
      return [...this.mix.bav].reverse()
    },
    deviationLabels() {
      // Most-recent-first: LineChart reverses category labels internally so the
      // oldest load renders on the left (same convention as MindsDashboard).
      return this.mix.bav.map((b) => String(b.n))
    },
    deviationData() {
      return CONCRETE_CONSTITUENTS.map((constituent) => ({
        name: this.$t(
          `views.dashboard.concrete.constituents.${constituent.label}`,
        ),
        values: this.chronologicalBav.map(
          (b) => +b[constituent.deviationKey].toFixed(2),
        ),
      }))
    },
    // ── Control bar charts ──
    chronologicalChantier() {
      return [...this.mix.chantier].reverse()
    },
    airItems() {
      const cible = this.mix.res.air.cible
      return this.chronologicalChantier.map((c) => ({
        name: String(c.n),
        value: c.air,
        status: this.airStatus(c.air, cible),
      }))
    },
    slumpItems() {
      const cible = this.mix.res.slump.cible
      return this.chronologicalChantier.map((c) => ({
        name: String(c.n),
        value: c.slump,
        status: this.slumpStatus(c.slump, cible),
      }))
    },
    tempItems() {
      return this.chronologicalChantier.map((c) => ({
        name: String(c.n),
        value: c.temp,
        status: this.tempStatus(c.temp),
      }))
    },
    statusMessages() {
      return {
        [chartStatus.SUCCESS]: this.$t(
          'views.dashboard.concrete.charts.statusOk',
        ),
        [chartStatus.WARNING]: this.$t(
          'views.dashboard.concrete.charts.statusWatch',
        ),
        [chartStatus.ERROR]: this.$t(
          'views.dashboard.concrete.charts.statusOut',
        ),
      }
    },
    // ── Daily production modal ──
    totalVolume() {
      return Object.values(CONCRETE_DATA).reduce(
        (total, d) => total + d.bav.reduce((sum, b) => sum + b.vol, 0),
        0,
      )
    },
    totalVolLabel() {
      return `${this.totalVolume.toFixed(1)} m³`
    },
    mixSummaries() {
      return Object.values(CONCRETE_DATA).map((d) => {
        const vol = d.bav.reduce((sum, b) => sum + b.vol, 0)
        return {
          label: d.label,
          mpa: d.mpa,
          proj: d.proj,
          vol,
          pct: Math.round((vol / this.totalVolume) * 100),
          count: d.count.total,
          active: d.label === this.selectedMix,
        }
      })
    },
  },
  methods: {
    average(values) {
      if (!values.length) return 0
      return values.reduce((sum, v) => sum + v, 0) / values.length
    },
    statusColor(status) {
      return this.$vuetify.theme.themes.light[status] || '#9E9D99'
    },
    selectMix(label) {
      this.selectedMix = label
      this.productionDialog = false
    },
    // ── Status helpers (thresholds ported from the HTML prototype) ──
    dosageStatus(dev, warn, crit) {
      const abs = Math.abs(dev)
      if (abs > crit) return chartStatus.ERROR
      if (abs > warn) return chartStatus.WARNING
      return chartStatus.SUCCESS
    },
    airStatus(value, cible) {
      const diff = Math.abs(value - cible)
      if (diff <= 0.5) return chartStatus.SUCCESS
      if (diff <= 1.0) return chartStatus.WARNING
      return chartStatus.ERROR
    },
    slumpStatus(value, cible) {
      const diff = Math.abs(value - cible)
      if (diff <= 20) return chartStatus.SUCCESS
      if (diff <= 30) return chartStatus.WARNING
      return chartStatus.ERROR
    },
    tempStatus(value) {
      if (value <= 25) return chartStatus.SUCCESS
      if (value <= 30) return chartStatus.WARNING
      return chartStatus.ERROR
    },
    transitStatus(value) {
      if (value < 60) return chartStatus.SUCCESS
      if (value <= 90) return chartStatus.WARNING
      return chartStatus.ERROR
    },
    percentStatus(value, cible, warnPct, critPct) {
      const pct = Math.abs((value - cible) / cible) * 100
      if (pct <= warnPct) return chartStatus.SUCCESS
      if (pct <= critPct) return chartStatus.WARNING
      return chartStatus.ERROR
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/styles/material-dashboard/variables.scss';

.concrete-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.concrete-topbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: $white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}
.concrete-select {
  max-width: 220px;
}
.concrete-select--mix {
  max-width: 320px;
}

// ── Fill layout : left column (tables) + 3-column chart area, 2 rows ──
.concrete-content {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 380px) repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 12px;
}

.concrete-tables {
  grid-row: 1 / span 2;
  grid-column: 1 / span 1;
  display: grid;
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 12px;
  min-height: 0;
}
.concrete-summary-card {
  height: auto !important; // hug content; the two tables above take the flexible space
}

.concrete-cell-deviation {
  grid-row: 1;
  grid-column: 2 / span 3;
  min-height: 0;
}
.concrete-cell-air {
  grid-row: 2;
  grid-column: 2;
  min-height: 0;
}
.concrete-cell-slump {
  grid-row: 2;
  grid-column: 3;
  min-height: 0;
}
.concrete-cell-temp {
  grid-row: 2;
  grid-column: 4;
  min-height: 0;
}

// Below the desktop breakpoint, stack everything and scroll.
@media (max-width: 1263px) {
  .concrete-content {
    display: flex;
    flex-direction: column;
    overflow: auto;
  }
  .concrete-tables {
    display: flex;
    flex-direction: column;
  }
  // Stacked layout: let the table cards hug their content (the page scrolls)
  // instead of collapsing against an auto-height parent.
  .concrete-table-dosage,
  .concrete-table-results {
    height: auto !important;
  }
  .concrete-cell-deviation,
  .concrete-cell-air,
  .concrete-cell-slump,
  .concrete-cell-temp {
    min-height: 300px;
  }
}

// ── Summary card (count KPIs + chantier averages) ──
.concrete-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $text-secondary;
  margin: 4px 0 6px;

  &:not(:first-child) {
    margin-top: 14px;
  }
}
.concrete-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
}
.concrete-kpi {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 9px 10px;
}
.concrete-kpi-label {
  font-size: 11px;
  color: $text-secondary;
}
.concrete-kpi-value {
  font-size: 20px;
  font-weight: 600;
}
.concrete-kpi-sub {
  font-size: 10px;
  color: $text-secondary;
}
.concrete-moy-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  padding: 3px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
}
.concrete-moy-value {
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

// ── Modal ──
.concrete-modal-header {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.concrete-modal-hint {
  font-size: 12px;
  color: $text-secondary;
  margin-bottom: 14px;
}
.concrete-mix-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  margin-bottom: 9px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
  &.active {
    border-color: var(--v-primary-base);
    background: rgba(64, 157, 185, 0.08);
  }
}
.concrete-mix-code {
  font-size: 13px;
  font-weight: 600;
}
.concrete-mix-proj {
  font-size: 12px;
  color: $text-secondary;
  margin-top: 1px;
}
.concrete-mix-bar-wrap {
  height: 4px;
  width: 160px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  margin-top: 6px;
  overflow: hidden;
}
.concrete-mix-bar {
  height: 4px;
  border-radius: 4px;
  transition: width 0.4s;
}
.concrete-mix-vol {
  font-size: 14px;
  font-weight: 600;
}
.concrete-mix-vol-lbl {
  font-size: 11px;
  color: $text-secondary;
}
.concrete-mix-count {
  font-size: 11px;
  color: $text-secondary;
  margin-top: 3px;
}
.concrete-modal-total {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 10px;
  padding-top: 12px;
  font-size: 14px;
  font-weight: 600;
}
</style>
